import React, { useMemo } from 'react';
import './UrlClusters.less';
import { getProgressColorByScore } from '../utils';
import { UrlCluster } from './types';
import { Button, Icon, Progress, Tag, Tooltip, Card, Row, Col } from 'antd';
import { getPueLevelColor, transformPueLevel } from './utils';
import useGetFailedChecksForCluster from '../failedChecks/hooks/useGetFailedChecksForCluster';
import { useNavigate } from "react-router-dom";
const { Meta } = Card;

type Props = {
    websiteGuid: string;
    cluster: UrlCluster;
    shouldLongPollFailedChecks?: boolean;
    onViewUrlsClicked: () => void;
};

export default function UrlClusterDetails({ websiteGuid, cluster, shouldLongPollFailedChecks, onViewUrlsClicked }: Props) {
    const navigate = useNavigate();
    const { failedChecksWithDetails } = useGetFailedChecksForCluster({
        websiteGuid,
        clusterGuid: cluster.guid,
        totalUrlsInCluster: cluster.monitored_urls,
        shouldLongPoll: shouldLongPollFailedChecks,
        shouldRefetchOnMount: shouldLongPollFailedChecks,
    });

    const topFailedChecks = useMemo(() => failedChecksWithDetails.sort((a, b) => b.impactScore - a.impactScore).slice(0, 3), [failedChecksWithDetails]);

    return (
        <Card
            className="page-group"
            type="inner"
            extra={
                <div className="pue">
                    <Tag
                        className="pue-level"
                        color={getPueLevelColor(cluster.pue_level)}
                    >
                        {transformPueLevel(cluster.pue_level)}
                    </Tag>
                    <Tooltip title={cluster.pue_reason}>
                        <Icon
                            className={`pue-reason-icon ${cluster.pue_reason ? '' : 'hidden'}`}
                            type="info-circle"
                            theme="filled"
                        />
                    </Tooltip>
                </div>
            }
            title={
                <Meta title={cluster.title} description={cluster.description} />
            }
        >
            <Row gutter={24}>
                <Col span={4}>
                    <div className="screenshot-container">
                        {cluster.screenshot_url ? <img className="cluster-screenshot" src={cluster.screenshot_url} alt="Screenshot" max-width="200" /> : <div className="no-screenshot" style={{ maxWidth: "200px", height: "120px" }}></div>}
                    </div>
                </Col>


                <Col span={8} className="issues">
                    <h4>High priority fixes</h4>
                    {topFailedChecks.length === 0 && <div className="no-fixes-message">No issues yet</div>}
                    {topFailedChecks.length > 0 && (
                        <>
                            <ul>
                                {topFailedChecks.map((failedCheck) => (
                                    <li
                                        key={failedCheck.testKey}
                                        className="failed"
                                    >
                                        <Icon type="stop" />

                                        <span className="link" onClick={() => navigate(`/deep-analysis/failed-check/${failedCheck.testKey}?w_guid=${websiteGuid}&cluster_guid=${cluster.guid}`)}>{failedCheck.seoIssue}</span>
                                    </li>

                                ))}

                            </ul>

                            <Button
                                className="view-more"
                                size="small"
                                onClick={onViewUrlsClicked}
                            >
                                {` View ${failedChecksWithDetails.length > 1 ? 'all' : ''} ${failedChecksWithDetails.length} Issue${failedChecksWithDetails.length > 1 ? 's' : ''}`}
                            </Button>
                        </>
                    )}
                </Col>

                <Col span={8} className="sample-urls">
                    <h4>Sample urls</h4>
                    {cluster.sample_urls?.slice(0, 3).map((sampleUrl) => (
                        <div key={sampleUrl.url} className="sample-url truncate">
                            <Icon type="link" />
                            <span className="link" onClick={() => navigate(`/deep-analysis/report?w_guid=${websiteGuid}&url_guid=${sampleUrl.guid}`)}>{sampleUrl.url}</span>
                        </div>
                    ))}

                    <Button
                        className="view-more"
                        size="small"
                        onClick={onViewUrlsClicked}
                    >
                        {` View ${cluster.monitored_urls > 1 ? 'all' : ''} ${cluster.monitored_urls} URL${cluster.monitored_urls > 1 ? 's' : ''}`}
                    </Button>
                </Col>




                <Col span={4} className="seo-score">
                    <div className="score-container">
                        <Progress
                            type="circle"
                            percent={cluster.avg_seo_score}
                            width={84}
                            strokeWidth={10}
                            strokeColor={getProgressColorByScore(cluster.avg_seo_score || 0)}
                            format={(percent) => `${percent}/100`}
                        />
                        <span className="score-label">SEO Score</span>
                    </div>
                </Col>
            </Row>
        </Card>
    );
}
